/* ============================================
   Tomorrow Night Eighties palette
   ============================================ */
:root {
  --bg: #2d2d2d;
  --bg-dark: #1d1d1d;
  --bg-darker: #121212;
  --fg: #cccccc;
  --fg-dim: #888888;
  --red: #f2777a;
  --orange: #f99157;
  --yellow: #ffcc66;
  --green: #99cc99;
  --cyan: #66cccc;
  --blue: #6699cc;
  --purple: #cc99cc;

  --glow-cyan: 0 0 6px var(--cyan), 0 0 14px rgba(102, 204, 204, 0.6);
  --glow-red: 0 0 6px var(--red), 0 0 14px rgba(242, 119, 122, 0.6);
  --glow-green: 0 0 6px var(--green), 0 0 14px rgba(153, 204, 153, 0.6);
  --glow-yellow: 0 0 6px var(--yellow), 0 0 14px rgba(255, 204, 102, 0.6);
  --glow-purple: 0 0 6px var(--purple), 0 0 14px rgba(204, 153, 204, 0.6);
  --glow-orange: 0 0 6px var(--orange), 0 0 14px rgba(249, 145, 87, 0.6);

  --tap-target: 96px;
}

/* ============================================
   Fonts
   ============================================ */
@font-face {
  font-family: 'Press Start 2P';
  src: url('/fonts/PressStart2P-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'VT323';
  src: url('/fonts/VT323-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

/* ============================================
   Base
   ============================================ */
*, *::before, *::after {
  box-sizing: border-box;
}

html, body, #root {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: var(--bg-dark);
}

body {
  font-family: 'VT323', monospace;
  color: var(--fg);
  overscroll-behavior: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  user-select: none;
}

button {
  font-family: inherit;
  touch-action: manipulation;
  cursor: pointer;
}

#root {
  display: flex;
  flex-direction: column;
}

/* ============================================
   CRT scanlines overlay
   ============================================ */
.crt-scanlines {
  position: fixed;
  inset: 0;
  z-index: 50;
  pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent 1px,
    rgba(0, 0, 0, 0.08) 1px,
    rgba(0, 0, 0, 0.08) 2px
  );
}

/* ============================================
   Screen flicker (subtle life)
   ============================================ */
@keyframes screen-flicker {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.97; }
}

.screen-flicker {
  animation: screen-flicker 0.12s infinite;
}

/* ============================================
   Neon glow utilities
   ============================================ */
.neon-cyan { color: var(--cyan); text-shadow: var(--glow-cyan); }
.neon-red { color: var(--red); text-shadow: var(--glow-red); }
.neon-green { color: var(--green); text-shadow: var(--glow-green); }
.neon-yellow { color: var(--yellow); text-shadow: var(--glow-yellow); }
.neon-purple { color: var(--purple); text-shadow: var(--glow-purple); }
.neon-orange { color: var(--orange); text-shadow: var(--glow-orange); }

.glow-cyan { box-shadow: var(--glow-cyan); }
.glow-red { box-shadow: var(--glow-red); }
.glow-green { box-shadow: var(--glow-green); }
.glow-yellow { box-shadow: var(--glow-yellow); }
.glow-purple { box-shadow: var(--glow-purple); }
.glow-orange { box-shadow: var(--glow-orange); }

/* ============================================
   Neon button (shared base)
   ============================================ */
.neon-btn {
  background: transparent;
  border: 2px solid var(--cyan);
  color: var(--cyan);
  text-shadow: var(--glow-cyan);
  font-family: 'Press Start 2P', monospace;
  letter-spacing: 1px;
  transition: background 75ms linear, color 75ms linear, box-shadow 120ms ease-out;
}

.neon-btn:active,
.neon-btn[aria-pressed="true"] {
  background: var(--cyan);
  color: var(--bg-dark);
  text-shadow: none;
  box-shadow: var(--glow-cyan);
}

.neon-btn.is-red { border-color: var(--red); color: var(--red); text-shadow: var(--glow-red); }
.neon-btn.is-red:active,
.neon-btn.is-red[aria-pressed="true"] { background: var(--red); color: var(--bg-dark); box-shadow: var(--glow-red); }

.neon-btn.is-green { border-color: var(--green); color: var(--green); text-shadow: var(--glow-green); }
.neon-btn.is-green:active,
.neon-btn.is-green[aria-pressed="true"] { background: var(--green); color: var(--bg-dark); box-shadow: var(--glow-green); }

.neon-btn.is-yellow { border-color: var(--yellow); color: var(--yellow); text-shadow: var(--glow-yellow); }
.neon-btn.is-yellow:active,
.neon-btn.is-yellow[aria-pressed="true"] { background: var(--yellow); color: var(--bg-dark); box-shadow: var(--glow-yellow); }

.neon-btn.is-purple { border-color: var(--purple); color: var(--purple); text-shadow: var(--glow-purple); }
.neon-btn.is-purple:active,
.neon-btn.is-purple[aria-pressed="true"] { background: var(--purple); color: var(--bg-dark); box-shadow: var(--glow-purple); }

/* ============================================
   Reduced motion
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  .screen-flicker {
    animation: none !important;
  }
}
